import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetProfileQuery, useLazyGetProfileQuery } from "store/services/auth";
import { useLogoutMutation } from "store/services/sso";

export const API_ACTIVITY_KEY = "api-activity";
export const LOGOUT_KEY = "user-logout";
export const DISABLE_AUTO_LOGOUT_KEY = "disable-auto-logout";

const DEFAULT_SESSION_DURATION = 120 * 60; // 2 hours in seconds
const POPUP_BEFORE_EXPIRY = 10 * 60; // show popup 10 mins before expiry

const useInactivityTimer = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

  const [isInactive, setIsInactive] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(DEFAULT_SESSION_DURATION);

  const intervalRef = useRef(null);

  const [logout, { isLoading: logoutLoading }] = useLogoutMutation();
  const [getProfile] = useLazyGetProfileQuery();

  /** Call this after every successful API request */
  const recordApiActivity = () => {
    const now = Date.now();
    localStorage.setItem(API_ACTIVITY_KEY, now.toString());
    setShowPopup(false);
    setIsInactive(false);
    setSecondsLeft(DEFAULT_SESSION_DURATION);
  };

  const logoutUser = async () => {
    try {
      await logout({ message: "You've been logged out after 2 hours of inactivity. Please log in again to continue." });
      localStorage.setItem(LOGOUT_KEY, Date.now().toString());
      setIsInactive(false);
      setShowPopup(false);
      setSecondsLeft(DEFAULT_SESSION_DURATION);
    } catch (err) {
      console.error("Error logging out", err);
    }
  };

  const continueSession = async () => {
    try {
      getProfile();
      recordApiActivity();
    } catch (err) {
      console.error("Error continuing session", err);
      logoutUser();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    if (sessionStorage.getItem(DISABLE_AUTO_LOGOUT_KEY) === "true") return;

    if (!localStorage.getItem(API_ACTIVITY_KEY)) {
      recordApiActivity();
    }

    const checkInactivity = () => {
      const lastActivity = parseInt(localStorage.getItem(API_ACTIVITY_KEY), 10);
      if (!lastActivity) return;

      const now = Date.now();
      const elapsedSeconds = Math.floor((now - lastActivity) / 1000);
      const remaining = DEFAULT_SESSION_DURATION - elapsedSeconds;

      setSecondsLeft(Math.max(0, remaining));

      // Show popup if within the warning window
      if (remaining <= POPUP_BEFORE_EXPIRY && remaining > 0) {
        setShowPopup(true);
      }

      // Auto logout if session expired
      if (remaining <= 0 && !isInactive) {
        setIsInactive(true);
        logoutUser();
      }
    };

    intervalRef.current = setInterval(checkInactivity, 1000);

    const handleStorage = (e) => {
      if (e.key === API_ACTIVITY_KEY) {
        setShowPopup(false);
        setIsInactive(false);
        setSecondsLeft(DEFAULT_SESSION_DURATION);
      }
      if (e.key === LOGOUT_KEY) {
        logoutUser();
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      clearInterval(intervalRef.current);
      window.removeEventListener("storage", handleStorage);
    };
  }, [isAuthenticated, isInactive]);

  return {
    isInactive,
    showPopup,
    secondsLeft, // ✅ countdown for popup
    logoutLoading,
    logoutUser,
    continueSession,
    recordApiActivity,
    setInactiveManually: () => setIsInactive(true),
  };
};

export default useInactivityTimer;
